// src/components/canine-teeth/SubImageAnnotator.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash'; // Importa debounce da lodash
import { saveSession, getSessionByImageNameAndEmail } from '../../indexedDB';
import { flushSync } from 'react-dom';
import axios from 'axios';

import {
  FaDownload,
  FaUndo,
  FaSave,
  FaMinus,
  FaEraser,
  FaPaintBrush,
  FaCircle,
  FaSearchMinus,
  FaCog,
  FaAdjust,
  FaSun,
} from 'react-icons/fa';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import '../tools.css';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CircularProgress from '@mui/material/CircularProgress';

function SubImageAnnotator({
  subImage,
  collectAnnotations,
  email,
  tipo,
  posizione,
  regione,
  sesso,
  realAge = '',
  defaultMode = 'line',
  allowModeChange = false,
  selectableColors = ['blue', 'red'],
  initialAnnotations = null,
  updateSessionsCount,
}) {

  const [ageErrorMarginMin, setAgeErrorMarginMin] = useState(null);
  const [ageErrorMarginMax, setAgeErrorMarginMax] = useState(null);
  const [sessoState, setSessoState] = useState(sesso || '');
  const [realAgeState, setRealAgeState] = useState(realAge || '');

  // Aggiungi questo riferimento per tracciare se il panning è avvenuto
  const hasPanned = useRef(false);

  // Aggiungi questa funzione per calcolare il centroide dei punti
  function computeCentroid(points) {
    const sum = points.reduce(
      (acc, point) => {
        acc.x += point.x;
        acc.y += point.y;
        return acc;
      },
      { x: 0, y: 0 }
    );
    return { x: sum.x / points.length, y: sum.y / points.length };
  }

  /**
 * Restituisce i quattro corner “veri” del poligono lungo le diagonali:
 * - topLeft     (NW)
 * - topRight    (NE)
 * - bottomLeft  (SW)
 * - bottomRight (SE)
 */
  function findDiagonalExtremes(polygon) {
    let topLeft = polygon[0];
    let topRight = polygon[0];
    let bottomLeft = polygon[0];
    let bottomRight = polygon[0];

    for (const p of polygon) {
      // NW: minimizza x+y
      if (p.x + p.y < topLeft.x + topLeft.y) topLeft = p;
      // NE: massimizza x−y
      if (p.x - p.y > topRight.x - topRight.y) topRight = p;
      // SW: massimizza y−x
      if (p.y - p.x > bottomLeft.y - bottomLeft.x) bottomLeft = p;
      // SE: massimizza x+y
      if (p.x + p.y > bottomRight.x + bottomRight.y) bottomRight = p;
    }

    return { topLeft, topRight, bottomLeft, bottomRight };
  }


  // Debounced saveCurrentSession function
  // eslint-disable-next-line
  const saveCurrentSessionDebounced = useCallback(
    debounce(async (sessionData) => {
      setIsSaving(true);
      await saveSession(sessionData);
      if (updateSessionsCount) {
        updateSessionsCount();
      }
      setIsSaving(false);
    }, 3000), // Debounce time of 3000ms (3 seconds)
    [] // Empty dependency array to create the function only once
  );

  // eslint-disable-next-line
  const [isSaving, setIsSaving] = useState(false);
  // Inizializza mouseDownPosition con valori null
  const [mouseDownPosition, setMouseDownPosition] = useState({ x: null, y: null });
  //const dragThreshold = 5;
  const panningThreshold = 5; // Pixel

  const tool = 'vertebral-body';

  // Memorizza selectableColors se viene passato come prop inline
  // eslint-disable-next-line
  const memoizedSelectableColors = useMemo(() => selectableColors, []);

  // Funzione helper per verificare se il mouse è vicino a un punto
const isMouseOverAnyPoint = (x, y) => {
  const threshold = pointRadius / zoomLevel + 2;
  return Object.entries(coordinates)
    .filter(([key]) => key !== 'polygon1')      // ignora polygon1
    .some(([, points]) =>
      points.some(point => {
        const dx = x - point.x;
        const dy = y - point.y;
        return Math.sqrt(dx*dx + dy*dy) < threshold;
      })
    );
};



  const colorMap = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color, index) => {
      map[color] = (index + 1).toString();
    });
    return map;
  }, [memoizedSelectableColors]);

  const lineKeys = useMemo(
    () => memoizedSelectableColors.map((color) => `line${colorMap[color]}`),
    [memoizedSelectableColors, colorMap]
  );

  const polygonKeys = useMemo(
    () => memoizedSelectableColors.map((color) => `polygon${colorMap[color]}`),
    [memoizedSelectableColors, colorMap]
  );

  const [coordinates, setCoordinates] = useState(() =>
    lineKeys.concat(polygonKeys).reduce((acc, key) => ({ ...acc, [key]: [] }), {})
  );

  const [ratios, setRatios] = useState({});
  const [i3mValue, setI3MValue] = useState(null); // Stato per memorizzare il valore I3M
  const [areas, setAreas] = useState({});
  const [estimatedAge, setEstimatedAge] = useState(null);
  const [currentLine, setCurrentLine] = useState(lineKeys[0]);
  // eslint-disable-next-line
  const [currentMode, setCurrentMode] = useState(defaultMode);
  const [lineThickness, setLineThickness] = useState(2);
  const [pointRadius, setPointRadius] = useState(5);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [activeSetting, setActiveSetting] = useState('');
  const [contrast, setContrast] = useState(100);
  const [brightness, setBrightness] = useState(100);
  const [isAILoading, setIsAILoading] = useState(false);
  const clickDisabled = useRef(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const minZoom = 0.5;
  const maxZoom = 5;

  // Stato per il panning e il dragging
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggingPoint, setDraggingPoint] = useState(null);

  // Memorizza subImage se necessario
  // eslint-disable-next-line
  const memoizedSubImage = useMemo(() => subImage, [subImage.name, subImage.dataURL]);

  // Funzione per calcolare il valore I3M
  const calculateI3M = useCallback(() => {
    if (ratios['line1']) {
      const toothHeight = ratios['line1'];
      let rootLength = 0;
      let rootCount = 0;

      if (ratios['line2']) {
        rootLength += ratios['line2'];
        rootCount++;
      }

      if (ratios['line3']) {
        rootLength += ratios['line3'];
        rootCount++;
      }

      if (rootCount > 0) {
        const I3M = rootLength / toothHeight;
        setI3MValue(I3M.toFixed(2));
      } else {
        setI3MValue(null);
      }
    }
  }, [ratios]);

  const lineKeyToColor = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color) => {
      const lineNumber = colorMap[color];
      map[`line${lineNumber}`] = color;
      map[`polygon${lineNumber}`] = color;
    });
    return map;
  }, [memoizedSelectableColors, colorMap]);

  const getLineColor = useCallback((lineKey) => {
    return lineKeyToColor[lineKey] || '#f0f0f0';
  }, [lineKeyToColor]);

  const handleLineChange = useCallback((newLine) => {
    setCurrentLine(newLine);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && draggingPoint) {
        // Handle dragging of points
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        // Update the position of the dragged point
        setCoordinates((prevCoordinates) => {
          const updatedCoordinates = { ...prevCoordinates };
          updatedCoordinates[draggingPoint.key][draggingPoint.pointIndex] = {
            x: imageX,
            y: imageY,
          };
          return updatedCoordinates;
        });
        clickDisabled.current = true; // Disable click while dragging

        // Set cursor to 'move' during dragging
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'move';
        }
      } else if (isPanning) {
        e.preventDefault(); // Prevent default behavior during panning

        const newPanOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        };
        setPanOffset(newPanOffset);
        hasPanned.current = true; // Indicate that panning has occurred

        // Set cursor to 'grabbing' during panning
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'grabbing';
        }
      } else if (mouseDownPosition.x !== null && mouseDownPosition.y !== null) {
        const deltaX = e.clientX - mouseDownPosition.x;
        const deltaY = e.clientY - mouseDownPosition.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > panningThreshold && zoomLevel > 1) {
          setIsPanning(true);
          setPanStart({
            x: mouseDownPosition.x - panOffset.x,
            y: mouseDownPosition.y - panOffset.y,
          });
          clickDisabled.current = true; // Prevent adding a point during panning
        } else {
          // Set cursor to 'crosshair' when not panning or dragging
          if (canvasRef.current) {
            canvasRef.current.style.cursor = 'crosshair';
          }
        }
      } else {
        // Verifica se il mouse è sopra un punto
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        if (isMouseOverAnyPoint(imageX, imageY)) {
          canvasRef.current.style.cursor = 'move'; // Cambia il cursore in 'move' quando sopra un punto
        } else {
          canvasRef.current.style.cursor = 'crosshair'; // Altrimenti, 'crosshair'
        }
      }
    },
    // eslint-disable-next-line
    [
      isDragging,
      draggingPoint,
      isPanning,
      panStart.x,
      panStart.y,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      mouseDownPosition,
      panningThreshold,
      coordinates, // Aggiungi coordinates alle dipendenze
      pointRadius,  // Aggiungi pointRadius alle dipendenze
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setIsDragging(false);
    setDraggingPoint(null);
    setMouseDownPosition({ x: null, y: null }); // Reset della posizione del mouse
    // Ripristina il cursore su 'crosshair'
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'crosshair';
    }
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      if (isDragging || isPanning || hasPanned.current) {
        hasPanned.current = false; // Reset dopo aver ignorato il click
        return; // Ignora il click
      }
      if (clickDisabled.current) {
        clickDisabled.current = false; // Reset del flag
        return; // Non aggiungere un nuovo punto
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = (x - panOffset.x) * scaleX / zoomLevel;
      const imageY = (y - panOffset.y) * scaleY / zoomLevel;

      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        const key =
          currentMode === 'line'
            ? currentLine
            : `polygon${currentLine.charAt(currentLine.length - 1)}`;

        if (currentMode === 'line' && updatedCoordinates[key].length < 2) {
          updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
        } else if (currentMode === 'polygon') {
          updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
        }

        return updatedCoordinates;
      });
      // Reset di hasPanned.current dopo aver aggiunto un punto
      hasPanned.current = false;
    },
    [
      isDragging,
      isPanning,
      clickDisabled,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      currentMode,
      currentLine,
    ]
  );

  const handleMouseDown = useCallback(
    (e) => {
      hasPanned.current = false;
      setMouseDownPosition({ x: e.clientX, y: e.clientY });

      // Calcola coordinate in spazio immagine
      const { left, top } = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - left;
      const y = e.clientY - top;
      const img = imageRef.current;
      const ix = (x - panOffset.x) * (img.naturalWidth / img.clientWidth) / zoomLevel;
      const iy = (y - panOffset.y) * (img.naturalHeight / img.clientHeight) / zoomLevel;

      // Cerco solo su line1/line2
      let found = null;
      for (let key of lineKeys) {
        for (let idx = 0; idx < coordinates[key].length; idx++) {
          const pt = coordinates[key][idx];
          if (Math.hypot(ix - pt.x, iy - pt.y) < pointRadius / zoomLevel + 2) {
            found = { key, idx };
            break;
          }
        }
        if (found) break;
      }

      if (found) {
        // Abilita drag solo per lineKeys
        setIsDragging(true);
        setDraggingPoint({ key: found.key, pointIndex: found.idx });
        clickDisabled.current = true;
        return;
      }

      // se non ho trovato niente, permetto il click-per-nuovo-punto
      clickDisabled.current = false;
    },
    [coordinates, lineKeys, panOffset.x, panOffset.y, zoomLevel, pointRadius]
  );




  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const zoomAmount = e.deltaY * -0.001;
      const newZoomLevel = Math.min(Math.max(zoomLevel + zoomAmount, minZoom), maxZoom);
      const zoomRatio = newZoomLevel / zoomLevel;

      const newPanOffset = {
        x: x - (x - panOffset.x) * zoomRatio,
        y: y - (y - panOffset.y) * zoomRatio,
      };

      setZoomLevel(newZoomLevel);
      setPanOffset(newPanOffset);
    },
    [zoomLevel, panOffset.x, panOffset.y, minZoom, maxZoom]
  );

  const handleReset = useCallback(() => {
    if (currentMode === 'line') {
      setCoordinates((prevCoordinates) => ({
        ...prevCoordinates,
        [currentLine]: [],
      }));
      setRatios((prevRatios) => {
        const updatedRatios = { ...prevRatios };
        delete updatedRatios[currentLine];
        return updatedRatios;
      });
    } else if (currentMode === 'polygon') {
      const polygonKey = `polygon${currentLine.charAt(currentLine.length - 1)}`;
      setCoordinates((prevCoordinates) => ({
        ...prevCoordinates,
        [polygonKey]: [],
      }));
      setAreas((prevAreas) => {
        const updatedAreas = { ...prevAreas };
        delete updatedAreas[polygonKey];
        return updatedAreas;
      });
    }
  }, [currentMode, currentLine]);

  // Funzione handleResetZoom già definita sopra
  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleUndoLastPoint = useCallback(() => {
    const key =
      currentMode === 'line'
        ? currentLine
        : `polygon${currentLine.charAt(currentLine.length - 1)}`;
    setCoordinates((prevCoordinates) => {
      const updatedCoordinates = { ...prevCoordinates };
      if (updatedCoordinates[key].length > 0) {
        updatedCoordinates[key] = updatedCoordinates[key].slice(0, -1);
      }
      return updatedCoordinates;
    });
  }, [currentMode, currentLine]);

  // Funzioni calcolate
  const calculatePolygonArea = useCallback((points) => {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area) / 2;
  }, []);

  const calculateLineLength = useCallback((points) => {
    if (points.length === 2) {
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    return 0;
  }, []);

  const saveCurrentSession = useCallback(() => {
    const sessionData = {
      subImage: {
        ...memoizedSubImage,
        name: memoizedSubImage.name,
      },
      tool,
      coordinates,
      ratios,
      areas,
      estimatedAge,
      ageErrorMarginMax,
      ageErrorMarginMin,
      email,
      sesso: sessoState,
      realAge: realAgeState,
      timestamp: new Date().toISOString(),
    };
    saveCurrentSessionDebounced(sessionData);
  }, [
    coordinates,
    ratios,
    areas,
    estimatedAge,
    memoizedSubImage,
    email,
    saveCurrentSessionDebounced,
    ageErrorMarginMax,
    ageErrorMarginMin,
    sessoState,
    realAgeState,
  ]);

  // Aggiorna l'uso di useEffect per il salvataggio
  useEffect(() => {
    saveCurrentSession();
  }, [coordinates, ratios, areas, estimatedAge, saveCurrentSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      saveCurrentSessionDebounced.cancel();
    };
  }, [saveCurrentSessionDebounced]);

  // useEffect per caricare la sessione salvata
  useEffect(() => {
    const loadSavedSession = async () => {
      const savedSession = await getSessionByImageNameAndEmail(
        memoizedSubImage.name,
        email
      );
      if (savedSession) {
        setCoordinates(savedSession.coordinates || {});
        setRatios(savedSession.ratios || {});
        setAreas(savedSession.areas || {});
        setEstimatedAge(savedSession.estimatedAge || null);
        setAgeErrorMarginMin(savedSession.ageErrorMarginMin || null);
        setAgeErrorMarginMax(savedSession.ageErrorMarginMax || null);
        setSessoState(savedSession.sesso || sessoState);
        setRealAgeState(savedSession.realAge || realAgeState);
      } else if (initialAnnotations) {
        setCoordinates(initialAnnotations.coordinates || {});
        setRatios(initialAnnotations.ratios || {});
        setAreas(initialAnnotations.areas || {});
        setEstimatedAge(initialAnnotations.estimatedAge || null);
        setAgeErrorMarginMin(savedSession.ageErrorMarginMin || null);
        setAgeErrorMarginMax(savedSession.ageErrorMarginMax || null);
        setSessoState(savedSession.sesso || sessoState);
        setRealAgeState(savedSession.realAge || realAgeState);
      }
    };
    loadSavedSession();
  }, [memoizedSubImage.name, email, initialAnnotations, sessoState, realAgeState]);

  // Modifica anche il calcolo dell'area nel useEffect corrispondente:
  useEffect(() => {
    const updatedAreas = {};
    polygonKeys.forEach((polygonKey) => {
      const points = coordinates[polygonKey];
      if (points.length >= 3) {
        const centroid = computeCentroid(points);
        const pointsWithAngles = points.map((point) => {
          const angle = Math.atan2(point.y - centroid.y, point.x - centroid.x);
          return { ...point, angle };
        });
        const sortedPoints = pointsWithAngles.sort((a, b) => a.angle - b.angle);
        const area = calculatePolygonArea(sortedPoints);
        updatedAreas[polygonKey] = area;
      } else if (areas[polygonKey]) {
        updatedAreas[polygonKey] = undefined;
      }
    });
    setAreas((prevAreas) => ({ ...prevAreas, ...updatedAreas }));

    const updatedRatios = {};
    lineKeys.forEach((lineKey) => {
      const points = coordinates[lineKey];
      if (points.length === 2) {
        const length = calculateLineLength(points);
        updatedRatios[lineKey] = length;
      } else if (ratios[lineKey]) {
        updatedRatios[lineKey] = undefined;
      }
    });
    setRatios((prevRatios) => ({ ...prevRatios, ...updatedRatios }));
    // eslint-disable-next-line
  }, [coordinates, polygonKeys, lineKeys, calculatePolygonArea, calculateLineLength]);

  // Nel tuo componente SubImageAnnotator, modifica il useEffect che disegna il canvas:
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const drawCanvas = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Disegna area AI (polygon1) solo contorno verde
        if (coordinates.polygon1 && coordinates.polygon1.length > 2) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(coordinates.polygon1[0].x, coordinates.polygon1[0].y);
          for (let i = 1; i < coordinates.polygon1.length; i++) {
            ctx.lineTo(coordinates.polygon1[i].x, coordinates.polygon1[i].y);
          }
          ctx.closePath();
          ctx.strokeStyle = 'green';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
        // Disegna le linee blu e rosse (editabili)
        const lineColors = { line1: 'blue', line2: 'red' };
        ['line1', 'line2'].forEach(lineKey => {
          const points = coordinates[lineKey];
          if (points && points.length === 2) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            ctx.lineTo(points[1].x, points[1].y);
            ctx.strokeStyle = lineColors[lineKey];
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            ctx.stroke();
            // Disegna i punti solo per le linee (editabili)
            points.forEach((point) => {
              ctx.beginPath();
              ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
              ctx.fillStyle = lineColors[lineKey];
              ctx.fill();
            });
            ctx.restore();
          }
        });
        // Disegna eventuali altre annotazioni manuali (escludi polygon1 se AI)
        Object.entries(coordinates).forEach(([key, points]) => {
          if (key !== 'polygon1' && !['line1', 'line2'].includes(key)) {
            const isLine = key.startsWith('line');
            const color = getLineColor(key);
            ctx.fillStyle = color;
            ctx.strokeStyle = color;
            if (isLine) {
              if (points.length > 0) {
                ctx.beginPath();
                ctx.moveTo(points[0].x, points[0].y);
                for (let i = 1; i < points.length; i++) {
                  ctx.lineTo(points[i].x, points[i].y);
                }
                ctx.lineWidth = lineThickness;
                ctx.stroke();
                points.forEach((point) => {
                  ctx.beginPath();
                  ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                  ctx.fill();
                });
              }
            } else {
              if (points.length > 0) {
                ctx.beginPath();
                ctx.moveTo(points[0].x, points[0].y);
                for (let i = 1; i < points.length; i++) {
                  ctx.lineTo(points[i].x, points[i].y);
                }
                ctx.closePath();
                ctx.globalAlpha = 0.3;
                ctx.fill();
                ctx.globalAlpha = 1.0;
                ctx.lineWidth = lineThickness;
                ctx.stroke();
                points.forEach((point) => {
                  ctx.beginPath();
                  ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                  ctx.fill();
                });
              }
            }
          }
        });
      };

      let animationFrameId = requestAnimationFrame(drawCanvas);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [coordinates, lineThickness, pointRadius, areas, getLineColor]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const handleSettingClick = (setting) => {
    setActiveSetting(activeSetting === setting ? '' : setting);
  };
  const handleContrastChange = (e) => setContrast(Number(e.target.value));
  const handleBrightnessChange = (e) => setBrightness(Number(e.target.value));
  const handleResetContrast = () => setContrast(100);
  const handleResetBrightness = () => setBrightness(100);
  const handleResetLineThickness = () => setLineThickness(1);
  const handleResetPointRadius = () => setPointRadius(2);

  // Funzione per resettare la vista e salvare l'immagine
  const handleDownloadAnnotatedImage = useCallback(() => {
    // Salva lo stato corrente di zoom e pan
    // const previousZoom = zoomLevel;
    // const previousPan = { ...panOffset };

    // Resetta zoom e pan sincronamente per assicurarti che il DOM si aggiorni prima della cattura
    flushSync(() => {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    });

    // Effettua la cattura dopo che lo stato è stato aggiornato
    html2canvas(imageContainerRef.current, { useCORS: true }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, memoizedSubImage.name);
        // // Ripristina lo stato originale di zoom e pan
        // setZoomLevel(previousZoom);
        // setPanOffset(previousPan);
      }, 'image/png', 1);
    });
  }, [memoizedSubImage.name]);

  const handleSaveAnnotationsJSON = useCallback(() => {
    const data = {
      imageName: memoizedSubImage.name,
      tool: 'Assessment of Pubertal Growth Spurt by The Fourth Cervical Vertebra Anterior and Posterior Body Height Projections (Vba)',
      annotations: coordinates,
      ratios: ratios,
      lines_ratio: i3mValue,  // Aggiungi il valore I3M al JSON
      gender: sessoState,
      timestamp: new Date().toISOString(),
      estimatedAge: estimatedAge,
      min_estimatedAge: ageErrorMarginMin,
      max_estimatedAge: ageErrorMarginMax,
      subImageDimensions: {
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      },
      email: email,
      realAge: realAgeState,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    saveAs(blob, `${memoizedSubImage.name.replace(/\.[^/.]+$/, '')}_annotations.json`);
  }, [
    memoizedSubImage,
    coordinates,
    ratios,
    i3mValue,
    email,
    sessoState,
    estimatedAge,
    ageErrorMarginMin,
    ageErrorMarginMax,
    realAgeState,
  ]);

  function findClosestValue(data, target) {
    return data.reduce((prev, curr) =>
      Math.abs(curr.vba - target) < Math.abs(prev.vba - target) ? curr : prev
    );
  }

  // Effetto per ricalcolare I3M quando i rapporti cambiano
  useEffect(() => {
    calculateI3M();
  }, [coordinates, ratios, calculateI3M]);

  useEffect(() => {
    if (i3mValue && sessoState) {

      const male_data = [
        { vba: 0.67, Q1: 1.57, Median: 1.81, Q3: 2.10 },
        { vba: 0.69, Q1: 1.47, Median: 1.69, Q3: 1.97 },
        { vba: 0.71, Q1: 1.36, Median: 1.57, Q3: 1.84 },
        { vba: 0.73, Q1: 1.25, Median: 1.46, Q3: 1.71 },
        { vba: 0.75, Q1: 1.14, Median: 1.34, Q3: 1.57 },
        { vba: 0.77, Q1: 1.03, Median: 1.22, Q3: 1.44 },
        { vba: 0.79, Q1: 0.92, Median: 1.10, Q3: 1.31 },
        { vba: 0.81, Q1: 0.81, Median: 0.98, Q3: 1.18 },
        { vba: 0.83, Q1: 0.70, Median: 0.87, Q3: 1.05 },
        { vba: 0.85, Q1: 0.59, Median: 0.75, Q3: 0.93 },
        { vba: 0.87, Q1: 0.48, Median: 0.63, Q3: 0.80 },
        { vba: 0.89, Q1: 0.37, Median: 0.51, Q3: 0.67 },
        { vba: 0.91, Q1: 0.25, Median: 0.39, Q3: 0.55 },
        { vba: 0.93, Q1: 0.14, Median: 0.28, Q3: 0.42 },
        { vba: 0.95, Q1: 0.02, Median: 0.16, Q3: 0.30 }
      ];

      const female_data = [
        { vba: 0.65, Q1: 3.55, Median: 3.73, Q3: 3.92 },
        { vba: 0.67, Q1: 3.38, Median: 3.55, Q3: 3.73 },
        { vba: 0.69, Q1: 3.21, Median: 3.37, Q3: 3.55 },
        { vba: 0.71, Q1: 3.04, Median: 3.19, Q3: 3.36 },
        { vba: 0.73, Q1: 2.86, Median: 3.02, Q3: 3.18 },
        { vba: 0.75, Q1: 2.69, Median: 2.84, Q3: 2.99 },
        { vba: 0.77, Q1: 2.52, Median: 2.66, Q3: 2.81 },
        { vba: 0.79, Q1: 2.35, Median: 2.48, Q3: 2.63 },
        { vba: 0.81, Q1: 2.18, Median: 2.31, Q3: 2.44 },
        { vba: 0.83, Q1: 2.00, Median: 2.13, Q3: 2.26 },
        { vba: 0.85, Q1: 1.83, Median: 1.95, Q3: 2.08 },
        { vba: 0.87, Q1: 1.66, Median: 1.77, Q3: 1.89 },
        { vba: 0.89, Q1: 1.49, Median: 1.60, Q3: 1.71 },
        { vba: 0.91, Q1: 1.31, Median: 1.42, Q3: 1.53 },
        { vba: 0.93, Q1: 1.14, Median: 1.24, Q3: 1.35 },
        { vba: 0.95, Q1: 0.96, Median: 1.06, Q3: 1.16 },
        { vba: 0.97, Q1: 0.79, Median: 0.88, Q3: 0.98 },
        { vba: 0.99, Q1: 0.61, Median: 0.71, Q3: 0.80 }
      ];

      const data = sessoState === 'M' ? male_data : female_data;
      const i3mNumericValue = parseFloat(i3mValue);
  
      if (i3mNumericValue > 0) {
        const closestValue = findClosestValue(data, i3mNumericValue);
        setEstimatedAge(closestValue.Median);
        setAgeErrorMarginMin(closestValue.Q1);
        setAgeErrorMarginMax(closestValue.Q3);
      }
    }
  }, [i3mValue, sessoState]);

  // AI annotation for vertebrae (C4 only)
  const handleMagicAI = async () => {
    setIsAILoading(true);
    try {
      const newCoordinates = {};
      lineKeys.concat(polygonKeys).forEach((key) => {
        newCoordinates[key] = [];
      });
      setCoordinates(newCoordinates);
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = memoizedSubImage.dataURL;
      image.onload = async () => {
        try {
          tempCanvas.width = image.width;
          tempCanvas.height = image.height;
          tempCtx.drawImage(image, 0, 0);
          tempCtx.filter = `contrast(${contrast}%) brightness(${brightness}%)`;
          tempCtx.drawImage(tempCanvas, 0, 0);
          const base64Image = tempCanvas.toDataURL('image/jpeg').split(',')[1];
          const url = 'https://serverless.roboflow.com/vert-cerv-kt9k9/1';
          const params = { api_key: 'CaA6CQPKRxRnK4QGzEvR' };
          const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
          let response;
          try {
            response = await axios({
              method: 'POST',
              url,
              params,
              data: base64Image,
              headers,
            });
          } catch (err) {
            setIsAILoading(false);
            alert('AI request failed: ' + err.message);
            return;
          }
          const data = response.data;
          const c4 = data.predictions.find(p => p.class === 'C4' && p.points && p.points.length >= 3);
          if (!c4) {
            setIsAILoading(false);
            alert('No C4 vertebra found.');
            return;
          }
          const adaptPoints = (points) => points.map(pt => ({ x: pt.x, y: pt.y }));
          const polygon1 = adaptPoints(c4.points);
          const { topLeft, topRight, bottomLeft, bottomRight } = findDiagonalExtremes(polygon1);
          setCoordinates(prev => ({
            ...prev,
            polygon1,
            line1: [topLeft, bottomLeft],
            line2: [topRight, bottomRight],
          }));
          setAreas(prev => ({ ...prev, polygon1: null }));
          setIsAILoading(false);
          alert('C4 vertebra annotated automatically!');
        } catch (innerError) {
          setIsAILoading(false);
          alert('Error during AI annotation: ' + innerError.message);
        }
      };
      image.onerror = () => {
        setIsAILoading(false);
        alert('Error loading image.');
      };
    } catch (error) {
      console.error(error);
      setIsAILoading(false);
      alert('Error with automatic annotation.');
    }
  };

  return (
    <div className="subimage-container">
      <h3 className="subimage-title">{memoizedSubImage.name}</h3>

      <div className='tooltips'>
        <div className="tooltip">
          <p>Use 🔵 color to select the Posterior Body Height Projections</p>
        </div>
        <div className="tooltip">
          <p>Use 🔴 color to select the Anterior Body Height Projections</p>
        </div>
      </div>

      <div className="subimage-topbar">
        <div className="icon-group">
          {lineKeys.map((line) => (
            <button
              key={line}
              onClick={() => handleLineChange(line)}
              className={`topbar-button ${currentLine === line ? 'active-line' : ''}`}
              title={`Color ${lineKeyToColor[line]}`}
              style={{ backgroundColor: getLineColor(line) }}
            >
              <FaMinus className="topbar-icon" />
            </button>
          ))}
        </div>
        {/* AI Magic Wand Button */}
        <div className="icon-group">
          <button
            onClick={handleMagicAI}
            className="topbar-button"
            title="Auto Annotate (AI) Vertebra C4"
            disabled={isAILoading}
          >
            {isAILoading
              ? <CircularProgress size={16} color="inherit" />
              : <AutoFixHighIcon className="topbar-icon" />}
          </button>
        </div>
        <div className="icon-group">
          <button onClick={handleUndoLastPoint} className="topbar-button" title="Undo Last Point">
            <FaUndo className="topbar-icon" />
          </button>
          <button onClick={handleReset} className="topbar-button" title="Reset">
            <FaEraser className="topbar-icon" />
          </button>
        </div>
        <div className="icon-group">
          <button
            onClick={handleResetZoom}
            className="topbar-button"
            title="Reset Zoom"
          >
            <FaSearchMinus className="topbar-icon" />
          </button>
        </div>
        <div className="icon-group">
          <button
            onClick={() => setShowSaveMenu(!showSaveMenu)}
            className="topbar-button"
            title="Save"
          >
            <FaSave className="topbar-icon" />
          </button>
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className={`topbar-button${showSettingsMenu ? ' active-settings' : ''}`}
            title="Settings"
          >
            <FaCog className="topbar-icon" />
          </button>
        </div>
      </div>

      {showSettingsMenu && (
        <div className="subimage-topbar settings-menu">
          <div className="settings-icons-container">
            <button
              className={`settings-icon-button${activeSetting === 'contrast' ? ' active' : ''}`}
              onClick={() => handleSettingClick('contrast')}
              title="Contrast"
            >
              <FaAdjust className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'brightness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('brightness')}
              title="Brightness"
            >
              <FaSun className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'lineThickness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('lineThickness')}
              title="Line Thickness"
            >
              <FaPaintBrush className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'pointRadius' ? ' active' : ''}`}
              onClick={() => handleSettingClick('pointRadius')}
              title="Point Size"
            >
              <FaCircle className="topbar-icon" />
            </button>
          </div>

          <div className={`slider-container ${activeSetting === 'contrast' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={contrast}
              onChange={handleContrastChange}
            />
            <div className="slider-value">
              {contrast}%
              <span className="reset-icon" onClick={handleResetContrast}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'brightness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={brightness}
              onChange={handleBrightnessChange}
            />
            <div className="slider-value">
              {brightness}%
              <span className="reset-icon" onClick={handleResetBrightness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'lineThickness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="1"
              max="5"
              value={lineThickness}
              onChange={(e) => setLineThickness(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {lineThickness}
              <span className="reset-icon" onClick={handleResetLineThickness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'pointRadius' ? 'visible' : ''}`}>
            <input
              type="range"
              min="2"
              max="10"
              value={pointRadius}
              onChange={(e) => setPointRadius(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {pointRadius}
              <span className="reset-icon" onClick={handleResetPointRadius}>🔄</span>
            </div>
          </div>
        </div>
      )}

      {showSaveMenu && (
        <div className="subimage-topbar save-menu">
          <div className="save-buttons-container">
            <button
              onClick={handleDownloadAnnotatedImage}
              className="save-button"
              title="Save Image"
            >
              <FaDownload className="button-icon" />
              Save Image
            </button>
            <button
              onClick={handleSaveAnnotationsJSON}
              className="save-button"
              title="Save Annotations (JSON)"
            >
              <FaSave className="button-icon" />
              Save Annotations
            </button>
          </div>
        </div>
      )}

      <div className="annotator-container">
        <div
          className="image-container"
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className="image-wrapper"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              src={memoizedSubImage.dataURL}
              alt="Selected sub-image"
              ref={imageRef}
              className="annotator-image"
              crossOrigin="anonymous"
              style={{ filter: `contrast(${contrast}%) brightness(${brightness}%)` }}
              onLoad={() => {
                const img = imageRef.current;
                const canvas = canvasRef.current;
                if (img && canvas) {
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                }
              }}
            />
            <canvas
              ref={canvasRef}
              className="annotator-canvas"
              onClick={handleCanvasClick}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <div className="coordinates-container">
          {lineKeys.map((line) => {
            const lineNumber = line.charAt(line.length - 1);
            const polygonKey = `polygon${lineNumber}`;
            const color = getLineColor(line);
            return (
              <div key={line} className="coordinate-box">
                <p className="point-text-small point-text-title" style={{ color: color }}>
                  {currentMode === 'line' ? 'Line' : 'Polygon'} ({color})
                </p>
                {currentMode === 'line' &&
                  coordinates[line].map((point, idx) => (
                    <p key={idx} className="point-text-small">
                      Pt {idx + 1}: ({Math.round(point.x)}, {Math.round(point.y)})
                    </p>
                  ))}
                {currentMode === 'polygon' &&
                  coordinates[polygonKey].map((point, idx) => (
                    <p key={idx} className="point-text-small">
                      Pt {idx + 1}: ({Math.round(point.x)}, {Math.round(point.y)})
                    </p>
                  ))}
                {currentMode === 'line' && ratios[line] && (
                  <p className="point-text-small">Length: {Math.round(ratios[line])} px</p>
                )}
                {currentMode === 'polygon' && areas[polygonKey] && (
                  <p className="point-text-small">Area: {Math.round(areas[polygonKey])} px²</p>
                )}

              </div>

            );

          })}

          {i3mValue && (
            <div className="coordinate-box">
              <p className="point-text-small point-text-title"><span style={{ color: 'red' }}>red</span>/<span style={{ color: 'blue' }}>blue</span> ratio</p>
              <h4>{i3mValue}</h4>
            </div>
          )}
        </div>



        {estimatedAge !== null && !isNaN(estimatedAge) && (
          <div className="results-footer">
            <div className="results-grid single-column">
              <div className="result-card">
                <h4>Assessment of Pubertal Growth Spurt</h4>
                <div className="age-comparison-modern">
                  <div className="value-item">
                    <span className="value">{ageErrorMarginMin}</span>
                    <span className="label">Min Age (yrs)</span>
                  </div>
                  <div className="value-item estimate">
                    <span className="value">{estimatedAge.toFixed(2)}</span>
                    <span className="label">Estimated Age (yrs)</span>
                  </div>
                  <div className="value-item">
                    <span className="value">{ageErrorMarginMax}</span>
                    <span className="label">Max Age (yrs)</span>
                  </div>
                  {realAgeState && (
                    <div className="value-item real-age">
                      <span className="value">{realAgeState}</span>
                      <span className="label">Real Age (yrs)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bibliography-section">
              <h5>Bibliography</h5>
              <div className="citation">
                <div className="title">The Fourth Cervical Vertebra Anterior and Posterior Body Height Projections (Vba) for the Assessment of Pubertal Growth Spurt</div>
                <div className="authors">Cameriere, R. et al.</div>
                <div className="journal">Applied Sciences 13, 1819 (2023)</div>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}

export default SubImageAnnotator;
